/**
 * useContractKanbanCounts - Totalizadores por coluna do Kanban de Contratos
 *
 * Uma única RPC devolve contagem e soma de valor dos negócios de todas as
 * colunas, evitando 7 consultas separadas de cabeçalho.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ContractFilters } from '@/hooks/useContractsPaginated';
import type { ContractStatusGroup } from '@/components/contracts/kanban/contractKanbanColumns';

// Logger centralizado
const logger = createLogger('ContractKanbanCounts');

/** Linha retornada pela RPC */
interface ContractKanbanCountRow {
  status_group: ContractStatusGroup;
  total_count: number;
  total_value: number;
}

/** Totais de uma coluna */
export interface ContractKanbanColumnTotals {
  count: number;
  value: number;
}

/**
 * Retorna um Map coluna -> { count, value } aplicando os mesmos filtros da listagem.
 */
export function useContractKanbanCounts(filters?: ContractFilters, enabled: boolean = true) {
  const { currentTenant } = useAuth();

  const query = useQuery({
    queryKey: ['contract-kanban-counts', currentTenant?.id, filters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_contracts_kanban_counts', {
        p_workspace_id: filters?.workspace_id || null,
        p_search: filters?.search || null,
        p_seller_id: filters?.seller_id || null,
        p_assigned_to: filters?.assigned_to || null,
        p_date_field: filters?.date_field || null,
        p_date_from: filters?.date_from || null,
        p_date_to: filters?.date_to || null,
        p_signature_provider_slug: filters?.signature_provider_slug || null,
        p_signer_role: filters?.signer_role || null,
        p_published_contract_id: filters?.published_contract_id || null,
        p_template_version_id: filters?.template_version_id || null,
        p_pending_signer_role: filters?.pending_signer_role || null,
        p_tag_ids: filters?.tag_ids || null,
        p_tenant_hint: currentTenant?.id || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar contagens do Kanban de Contratos:', error.message);
        throw error;
      }

      const map = new Map<ContractStatusGroup, ContractKanbanColumnTotals>();
      ((data || []) as ContractKanbanCountRow[]).forEach((row) => {
        map.set(row.status_group, {
          count: Number(row.total_count) || 0,
          value: Number(row.total_value) || 0,
        });
      });
      return map;
    },
    enabled: !!currentTenant?.id && enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60_000,
  });

  return {
    countsMap: query.data ?? new Map<ContractStatusGroup, ContractKanbanColumnTotals>(),
    isLoading: query.isLoading,
  };
}
